/**
 * Installable-app plumbing: the install prompt, push-notification
 * subscription for this device, and online/offline state.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { api, isReachable, onReachabilityChange, setReachable } from './api.ts';

// ── Install ─────────────────────────────────────────────────────────

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const installListeners = new Set<() => void>();
const notifyInstall = () => installListeners.forEach((l) => l());

/** Call once at startup: Chrome/Edge/Android fire this only once, early. */
export function captureInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notifyInstall();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notifyInstall();
  });
}

export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const isIos = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS reports itself as a Mac with touch.
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export function useInstall() {
  const canPrompt = useSyncExternalStore(
    (l) => {
      installListeners.add(l);
      return () => installListeners.delete(l);
    },
    () => deferredPrompt !== null,
  );
  return {
    installed: isStandalone(),
    canPrompt,
    /** iPhone/iPad: no prompt exists; people use Share → Add to Home Screen. */
    iosManual: isIos() && !isStandalone(),
    async install() {
      if (!deferredPrompt) return false;
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      deferredPrompt = null;
      notifyInstall();
      return outcome === 'accepted';
    },
  };
}

// ── Push notifications on this device ───────────────────────────────

export type PushState =
  | 'unsupported' // browser has no Web Push
  | 'needs-install' // iPhone/iPad: push works only from the home-screen app
  | 'server-off' // VAPID keys not configured on the server
  | 'denied' // the person blocked notifications in the browser
  | 'off'
  | 'on';

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

const pushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

async function registration() {
  return navigator.serviceWorker.ready;
}

export async function getPushState(): Promise<PushState> {
  if (!pushSupported()) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  const { enabled } = await api.get<{ enabled: boolean }>('/api/notifications/push/key');
  if (!enabled) return 'server-off';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? 'on' : 'off';
}

/** Ask permission (if needed), subscribe this device and tell the server. */
export async function enablePush(): Promise<PushState> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';
  const { publicKey } = await api.get<{ publicKey: string }>('/api/notifications/push/key');
  const reg = await registration();
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(publicKey),
    }));
  await api.post('/api/notifications/push/subscribe', sub.toJSON());
  return 'on';
}

export async function disablePush(): Promise<PushState> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.post('/api/notifications/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => undefined);
    await sub.unsubscribe();
  }
  return 'off';
}

export function usePushState(enabled = true) {
  const [state, setState] = useState<PushState | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    getPushState()
      .then((s) => !cancelled && setState(s))
      .catch(() => !cancelled && setState('unsupported'));
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return [state, setState] as const;
}

// ── Connectivity ────────────────────────────────────────────────────

/**
 * False when the device is offline or the last API request couldn't reach
 * the server. Coming back online triggers a quick re-check.
 */
export function useOnline() {
  return useSyncExternalStore(
    (l) => {
      const recheck = () => {
        l();
        void fetch('/api/health', { cache: 'no-store' }).then(
          (r) => setReachable(r.ok),
          () => setReachable(false),
        );
      };
      window.addEventListener('online', recheck);
      window.addEventListener('offline', l);
      const off = onReachabilityChange(l);
      // While the server is unreachable, keep checking so the notice clears
      // on its own once it's back.
      const timer = setInterval(() => {
        if (!isReachable() && navigator.onLine) recheck();
      }, 20_000);
      return () => {
        window.removeEventListener('online', recheck);
        window.removeEventListener('offline', l);
        off();
        clearInterval(timer);
      };
    },
    () => navigator.onLine && isReachable(),
  );
}
